import { apiRequest } from '../http';
import { IResultsRepository } from './IResultsRepository';
import {
  ImportBatch,
  SnapshotDetail,
  CreateImportBatchInput,
  CreateImportBatchResult,
  ValidationError,
  AccountDetailStagingRow,
  UpdateStagingRowInput,
  ListStagingFilters,
  AccountDetail,
  UpdateAccountDetailInput,
  ListAccountDetailFilters,
  PagedResult,
  SummaryFilters,
  SummaryRow,
  SummaryDetailFilters,
  SummaryDetailRow,
  ClientComparisonFilters,
  ClientComparisonRow,
  AvailableMonth,
  ClientMapping,
  CreateClientMappingInput,
  UpdateClientMappingInput,
  AccountCatalogItem,
  CreateAccountInput,
  UpdateAccountInput,
  ServiceType,
  CreateServiceTypeInput,
  UpdateServiceTypeInput,
} from './types';

export class RemoteResultsRepository implements IResultsRepository {
  constructor(private getToken: () => Promise<string | null>) {}

  async listBatches(): Promise<ImportBatch[]> {
    const token = await this.getToken();
    return apiRequest('/api/results/batches', { token });
  }
  async getBatch(id: string): Promise<ImportBatch | null> {
    const token = await this.getToken();
    return apiRequest(`/api/results/batches/${id}`, { token });
  }
  async listSnapshotDetail(importBatchId: string): Promise<SnapshotDetail[]> {
    const token = await this.getToken();
    return apiRequest(`/api/results/batches/${importBatchId}/snapshot`, { token });
  }
  async listValidationErrors(importBatchId: string): Promise<ValidationError[]> {
    const token = await this.getToken();
    return apiRequest(`/api/results/batches/${importBatchId}/validation-errors`, { token });
  }
  async createImportBatch(input: CreateImportBatchInput): Promise<CreateImportBatchResult> {
    const token = await this.getToken();
    return apiRequest('/api/results/batches', { method: 'POST', body: input, token });
  }
  async deleteMonthProcessing(importBatchId: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/results/batches/${importBatchId}`, { method: 'DELETE', token });
  }
  async listAvailableMonths(): Promise<AvailableMonth[]> {
    const token = await this.getToken();
    return apiRequest('/api/results/available-months', { token });
  }

  async listStaging(filters: ListStagingFilters): Promise<AccountDetailStagingRow[]> {
    const token = await this.getToken();
    const params = new URLSearchParams();
    if (filters.importBatchId) params.set('importBatchId', filters.importBatchId);
    if (filters.mes) params.set('mes', filters.mes);
    if (filters.anio) params.set('anio', String(filters.anio));
    if (filters.status) params.set('status', filters.status);
    return apiRequest(`/api/results/staging?${params.toString()}`, { token });
  }
  async updateStagingRow(id: string, patch: UpdateStagingRowInput): Promise<AccountDetailStagingRow> {
    const token = await this.getToken();
    return apiRequest(`/api/results/staging/${id}`, { method: 'PATCH', body: patch, token });
  }
  async deleteStagingRow(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/results/staging/${id}`, { method: 'DELETE', token });
  }
  async confirmMonth(mes: string, anio: number): Promise<number> {
    const token = await this.getToken();
    const res = await apiRequest<{ confirmed: number }>('/api/results/staging/confirm', { method: 'POST', body: { mes, anio }, token });
    return res.confirmed;
  }

  async listAccountDetail(filters: ListAccountDetailFilters): Promise<PagedResult<AccountDetail>> {
    const token = await this.getToken();
    const params = new URLSearchParams();
    if (filters.mes) params.set('mes', filters.mes);
    if (filters.anio) params.set('anio', String(filters.anio));
    if (filters.cliente) params.set('cliente', filters.cliente);
    if (filters.tipoServicio) params.set('tipoServicio', filters.tipoServicio);
    params.set('includeEmpty', String(!!filters.includeEmpty));
    params.set('page', String(filters.page ?? 1));
    params.set('pageSize', String(filters.pageSize ?? 50));
    return apiRequest(`/api/results/account-detail?${params.toString()}`, { token });
  }
  async updateAccountDetail(id: string, patch: UpdateAccountDetailInput): Promise<AccountDetail> {
    const token = await this.getToken();
    return apiRequest(`/api/results/account-detail/${id}`, { method: 'PATCH', body: patch, token });
  }
  async deleteAccountDetail(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/results/account-detail/${id}`, { method: 'DELETE', token });
  }

  async getSummary(filters: SummaryFilters): Promise<SummaryRow[]> {
    const token = await this.getToken();
    const params = new URLSearchParams();
    params.set('anio', String(filters.anio));
    if (filters.tiposServicio && filters.tiposServicio.length > 0) params.set('tiposServicio', filters.tiposServicio.join(','));
    if (filters.clientes && filters.clientes.length > 0) params.set('clientes', filters.clientes.join(','));
    return apiRequest(`/api/results/summary?${params.toString()}`, { token });
  }

  async getSummaryDetail(filters: SummaryDetailFilters): Promise<SummaryDetailRow[]> {
    const token = await this.getToken();
    const params = new URLSearchParams();
    params.set('anio', String(filters.anio));
    params.set('tipoCuenta', filters.tipoCuenta);
    if (filters.tiposServicio && filters.tiposServicio.length > 0) params.set('tiposServicio', filters.tiposServicio.join(','));
    if (filters.clientes && filters.clientes.length > 0) params.set('clientes', filters.clientes.join(','));
    return apiRequest(`/api/results/summary/detail?${params.toString()}`, { token });
  }

  async getClientComparison(filters: ClientComparisonFilters): Promise<ClientComparisonRow[]> {
    const token = await this.getToken();
    const params = new URLSearchParams();
    params.set('anio', String(filters.anio));
    if (filters.tiposServicio && filters.tiposServicio.length > 0) params.set('tiposServicio', filters.tiposServicio.join(','));
    if (filters.clientes && filters.clientes.length > 0) params.set('clientes', filters.clientes.join(','));
    if (filters.meses && filters.meses.length > 0) params.set('meses', filters.meses.join(','));
    return apiRequest(`/api/results/client-comparison?${params.toString()}`, { token });
  }

  async listDistinctClients(): Promise<string[]> {
    const token = await this.getToken();
    return apiRequest('/api/results/clients', { token });
  }

  async listClientMappings(): Promise<ClientMapping[]> {
    const token = await this.getToken();
    return apiRequest('/api/results/client-mappings', { token });
  }
  async createClientMapping(input: CreateClientMappingInput): Promise<ClientMapping> {
    const token = await this.getToken();
    return apiRequest('/api/results/client-mappings', { method: 'POST', body: input, token });
  }
  async updateClientMapping(id: string, patch: UpdateClientMappingInput): Promise<ClientMapping> {
    const token = await this.getToken();
    return apiRequest(`/api/results/client-mappings/${id}`, { method: 'PATCH', body: patch, token });
  }
  async deleteClientMapping(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/results/client-mappings/${id}`, { method: 'DELETE', token });
  }

  async listAccounts(): Promise<AccountCatalogItem[]> {
    const token = await this.getToken();
    return apiRequest('/api/results/accounts', { token });
  }
  async createAccount(input: CreateAccountInput): Promise<AccountCatalogItem> {
    const token = await this.getToken();
    return apiRequest('/api/results/accounts', { method: 'POST', body: input, token });
  }
  async updateAccount(id: string, patch: UpdateAccountInput): Promise<AccountCatalogItem> {
    const token = await this.getToken();
    return apiRequest(`/api/results/accounts/${id}`, { method: 'PATCH', body: patch, token });
  }
  async deleteAccount(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/results/accounts/${id}`, { method: 'DELETE', token });
  }

  async listServiceTypes(): Promise<ServiceType[]> {
    const token = await this.getToken();
    return apiRequest('/api/results/service-types', { token });
  }
  async createServiceType(input: CreateServiceTypeInput): Promise<ServiceType> {
    const token = await this.getToken();
    return apiRequest('/api/results/service-types', { method: 'POST', body: input, token });
  }
  async updateServiceType(id: string, patch: UpdateServiceTypeInput): Promise<ServiceType> {
    const token = await this.getToken();
    return apiRequest(`/api/results/service-types/${id}`, { method: 'PATCH', body: patch, token });
  }
  async deleteServiceType(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/results/service-types/${id}`, { method: 'DELETE', token });
  }
}
